import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { chartSpansFreezeBoundary, getFrozenLayout } from '@/app/gridGL/pixiApp/frozenLayout';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { JsHtmlOutput } from '@/app/quadratic-core-types';
import { colors } from '@/app/theme/colors';
import { BaseTexture, Container, Graphics, Point, Rectangle, Sprite, Texture } from 'pixi.js';

const BORDER_WIDTH = 1;

interface ChartSprite {
  container: Container;
  sprite: Sprite;
  border: Graphics;
  data: JsHtmlOutput;
  bounds: Rectangle;
  textureLoading: boolean;
  textureReady: boolean;
  // Texture currently mounted on `sprite` (when loaded from a data URL).
  // Tracked so we can destroy it explicitly when the chart image changes or
  // the chart is removed - data URLs are unique per image and the underlying
  // BaseTexture would otherwise be retained indefinitely by Pixi's caches.
  loadedTexture?: Texture;
}

/**
 * Renders chart placeholders as Pixi sprites on mobile devices.
 * This avoids loading heavy iframes which can crash mobile browsers.
 * Shows the pre-generated chart image if available, otherwise shows a placeholder icon.
 */
export class MobileChartPlaceholders extends Container {
  private charts: Map<string, ChartSprite> = new Map();
  // Tracks which chart placeholders are currently activated (DOM iframe is
  // showing instead). Kept separate from `JsHtmlOutput` so we don't shadow
  // future fields added to the core type.
  private activatedCharts: Set<string> = new Set();
  private isMobile: boolean;

  constructor() {
    super();
    this.isMobile = isMobileMode();

    if (this.isMobile) {
      events.on('htmlOutput', this.handleHtmlOutput);
      events.on('htmlUpdate', this.handleHtmlUpdate);
      events.on('changeSheet', this.handleChangeSheet);
      events.on('sheetOffsetsUpdated', this.handleOffsetsUpdated);
    }
  }

  private getKey(data: JsHtmlOutput): string {
    return `${data.sheet_id}:${data.x}:${data.y}`;
  }

  private handleHtmlOutput = (htmlCells: JsHtmlOutput[]) => {
    if (!this.isMobile) return;

    // Remove charts that no longer exist
    const newKeys = new Set(htmlCells.filter((c) => c.html).map((c) => this.getKey(c)));
    for (const [key, chart] of this.charts) {
      if (!newKeys.has(key)) {
        this.removeChart(key, chart);
      }
    }

    // Add or update charts
    for (const data of htmlCells) {
      if (data.html) {
        this.addOrUpdateChart(data);
      }
    }
  };

  private handleHtmlUpdate = (data: JsHtmlOutput) => {
    if (!this.isMobile) return;

    const key = this.getKey(data);

    if (data.html) {
      this.addOrUpdateChart(data);
    } else {
      const chart = this.charts.get(key);
      if (chart) {
        this.removeChart(key, chart);
      }
    }
  };

  private removeChart(key: string, chart: ChartSprite) {
    this.evictLoadedTexture(chart);
    chart.container.destroy({ children: true });
    this.charts.delete(key);
    this.activatedCharts.delete(key);
  }

  private evictLoadedTexture(chart: ChartSprite) {
    const texture = chart.loadedTexture;
    if (!texture) return;
    chart.loadedTexture = undefined;
    if (chart.sprite.texture === texture) {
      chart.sprite.texture = Texture.EMPTY;
    }
    if (texture !== Texture.EMPTY) {
      // destroy(true) tears down the BaseTexture and removes both the Texture
      // and BaseTexture from Pixi's internal caches via their tracked cache ids.
      texture.destroy(true);
    }
  }

  private handleChangeSheet = () => {
    if (!this.isMobile) return;
    this.updateVisibility();
  };

  private handleOffsetsUpdated = (sheetId: string) => {
    if (!this.isMobile) return;

    for (const chart of this.charts.values()) {
      if (chart.data.sheet_id === sheetId) {
        this.updateChartPosition(chart);
      }
    }
  };

  private addOrUpdateChart(data: JsHtmlOutput) {
    const key = this.getKey(data);
    let chart = this.charts.get(key);

    if (chart) {
      // Update existing chart - mark texture as needing reload if chart_image changed
      const imageChanged = chart.data.chart_image !== data.chart_image;
      chart.data = data;
      if (imageChanged) {
        // Evict the previous texture from the cache so the data URL doesn't leak
        this.evictLoadedTexture(chart);
        chart.textureReady = false;
        chart.textureLoading = false;
      }
      this.updateChartPosition(chart);
    } else {
      // Create new chart
      chart = this.createChart(data);
      this.charts.set(key, chart);
      this.addChild(chart.container);
    }

    if (chartSpansFreezeBoundary(data)) {
      this.ensureChartTexture(chart);
    }
    this.updateChartVisibility(chart);
  }

  private createChart(data: JsHtmlOutput): ChartSprite {
    const container = new Container();

    // Create border/background
    const border = new Graphics();
    container.addChild(border);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.visible = false;
    container.addChild(sprite);

    const chart: ChartSprite = {
      container,
      sprite,
      border,
      data,
      bounds: new Rectangle(),
      textureLoading: false,
      textureReady: false,
    };

    this.updateChartPosition(chart);

    return chart;
  }

  private ensureChartTexture(chart: ChartSprite) {
    if (chart.textureReady || chart.textureLoading || !chart.data.chart_image) return;
    chart.textureLoading = true;

    const key = this.getKey(chart.data);
    const baseTexture = new BaseTexture(chart.data.chart_image);
    const mount = () => {
      if (!this.charts.has(key)) {
        baseTexture.destroy();
        return;
      }
      this.evictLoadedTexture(chart);
      const texture = new Texture(baseTexture);
      chart.loadedTexture = texture;
      chart.sprite.texture = texture;
      chart.sprite.visible = true;
      chart.textureLoading = false;
      chart.textureReady = true;
      this.updateChartPosition(chart);
    };

    if (baseTexture.valid) {
      mount();
      return;
    }

    baseTexture.once('loaded', mount);
    baseTexture.once('error', () => {
      chart.textureLoading = false;
    });
  }

  private updateChartPosition(chart: ChartSprite) {
    const { container, sprite, border, data } = chart;

    const sheet = sheets.getById(data.sheet_id);
    if (!sheet) return;

    // Get actual pixel dimensions from sheet (y+1 to skip header row)
    const rect = sheet.getScreenRectangle(Number(data.x), Number(data.y) + 1, data.w ?? 1, (data.h ?? 1) - 1);

    // Position container and store bounds for culling
    container.x = rect.x;
    container.y = rect.y;
    chart.bounds.x = rect.x;
    chart.bounds.y = rect.y;
    chart.bounds.width = rect.width;
    chart.bounds.height = rect.height;

    // Draw border/background
    border.clear();
    border.lineStyle(BORDER_WIDTH, colors.htmlPlaceholderThumbnailBorderColor, 1);
    border.beginFill(colors.htmlPlaceholderThumbnailColor);
    border.drawRect(0, 0, rect.width, rect.height);
    border.endFill();

    const hasImage =
      chart.textureReady && sprite.texture.valid && sprite.texture.width > 0 && sprite.texture.height > 0;
    sprite.visible = hasImage;
    if (hasImage) {
      // Scale sprite to fit the cell area while preserving aspect ratio
      const texture = sprite.texture;
      if (texture.valid && texture.width > 0 && texture.height > 0) {
        const imageAspect = texture.width / texture.height;
        const cellAspect = rect.width / rect.height;

        let renderWidth: number;
        let renderHeight: number;

        if (imageAspect > cellAspect) {
          renderWidth = rect.width - BORDER_WIDTH * 2;
          renderHeight = renderWidth / imageAspect;
        } else {
          renderHeight = rect.height - BORDER_WIDTH * 2;
          renderWidth = renderHeight * imageAspect;
        }

        sprite.width = renderWidth;
        sprite.height = renderHeight;
        sprite.x = BORDER_WIDTH + (rect.width - BORDER_WIDTH * 2 - renderWidth) / 2;
        sprite.y = (rect.height - renderHeight) / 2;
      } else {
        // Texture not yet valid, wait for it
        sprite.texture.once('update', () => {
          this.updateChartPosition(chart);
        });
      }
    } else if (chart.textureReady) {
      sprite.texture.once('update', () => this.updateChartPosition(chart));
    }
  }

  private updateChartVisibility(chart: ChartSprite) {
    const key = this.getKey(chart.data);
    const isCurrentSheet = chart.data.sheet_id === sheets.current;
    chart.container.visible = isCurrentSheet && !this.activatedCharts.has(key);
  }

  private updateVisibility() {
    for (const chart of this.charts.values()) {
      this.updateChartVisibility(chart);
    }
  }

  /**
   * Called when an HtmlCell is activated (user interacts with it).
   * Hides the Pixi placeholder so the DOM iframe can be shown.
   */
  setActivated(sheetId: string, x: bigint, y: bigint, activated: boolean) {
    const key = `${sheetId}:${x}:${y}`;
    const chart = this.charts.get(key);
    if (chart) {
      if (activated) {
        this.activatedCharts.add(key);
      } else {
        this.activatedCharts.delete(key);
      }
      this.updateChartVisibility(chart);
    }
  }

  /** Whether the live DOM iframe is showing for this chart (selected on mobile). */
  isChartActivated(data: JsHtmlOutput): boolean {
    return this.activatedCharts.has(this.getKey(data));
  }

  /**
   * Check if a point intersects with any chart placeholder.
   * Used for hit testing on mobile.
   */
  getChartAt(worldX: number, worldY: number): JsHtmlOutput | undefined {
    for (const chart of this.charts.values()) {
      if (chart.data.sheet_id !== sheets.current) continue;
      if (!chart.container.visible) continue;

      if (intersects.rectanglePoint(chart.bounds, new Point(worldX, worldY))) {
        return chart.data;
      }
    }
    return undefined;
  }

  update() {
    if (!this.isMobile) return;

    const viewportBounds = pixiApp.viewport.getVisibleBounds();
    const torn = !!getFrozenLayout()?.torn;

    for (const [key, chart] of this.charts) {
      // Skip charts on other sheets
      if (chart.data.sheet_id !== sheets.current) {
        chart.container.visible = false;
        continue;
      }

      const spansFreeze = chartSpansFreezeBoundary(chart.data);
      if (spansFreeze) {
        this.ensureChartTexture(chart);
      }

      // Skip activated charts (DOM iframe is showing instead; torn slices use
      // ChartPlaceholders while the iframe covers the contiguous region).
      if (this.activatedCharts.has(key)) {
        chart.container.visible = false;
        continue;
      }

      const pinnedWhileTorn = torn && spansFreeze;
      const isInViewport = intersects.rectangleRectangle(chart.bounds, viewportBounds);
      chart.container.visible = pinnedWhileTorn || isInViewport;

      if (chart.container.visible) {
        this.updateChartPosition(chart);
      }
    }
  }

  destroy() {
    if (this.isMobile) {
      events.off('htmlOutput', this.handleHtmlOutput);
      events.off('htmlUpdate', this.handleHtmlUpdate);
      events.off('changeSheet', this.handleChangeSheet);
      events.off('sheetOffsetsUpdated', this.handleOffsetsUpdated);
    }

    for (const chart of this.charts.values()) {
      this.evictLoadedTexture(chart);
    }
    this.charts.clear();
    this.activatedCharts.clear();

    super.destroy({ children: true });
  }
}
